import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Bookmark, Send, Share2 } from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Athlete } from '@/components/athletes/AthleteCard'
import type { DirectMessageData } from '@/components/athletes/SendMessageModal'
import { SendOfferModal, OfferData } from '@/components/athletes/SendOfferModal'
import { offerRequestBody } from '@/components/athletes/offerRequest'
import {
  useCreateCollaborationMutation,
  useCreateDirectMessageThreadMutation,
  useGetAthleteMarketplaceProfileQuery,
  useGetCampaignsQuery,
  useSetShortlistedMutation,
} from '@/redux/marketerApi'
import { MarketplaceProfile, decodePick, type BookPick } from '@adthlete/shared-frontend'
import { cn } from '@/lib/utils'
import { ApplicationActions } from '@/components/applications/ApplicationActions'
import { money } from '@/lib/dealTypes'
import { useGetApplicationsQuery } from '@/redux/marketerApi'

const NO_CAMPAIGN = 'none'

/**
 * An athlete's page in the brand's Marketplace. Pick the campaign you're looking at them for to
 * see why they fit it; then make an offer (from their rate card or custom), message them, save
 * them to the shortlist or share the page.
 */
const AthleteProfile = () => {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const campaignId = searchParams.get('campaignId')
  // Opened from Incoming: the athlete's pitch (or application) is what the brand is answering
  const applicationId = searchParams.get('application')
  const { data: applications = [] } = useGetApplicationsQuery(undefined, { skip: !applicationId })
  const application = applicationId ? applications.find((a) => a.id === applicationId) : undefined
  const { toast } = useToast()

  const { data: campaigns = [] } = useGetCampaignsQuery()
  const openCampaigns = campaigns.filter((c) => c.status === 'active' || c.status === 'draft')
  const campaign = campaigns.find((c) => c.id === campaignId) ?? null
  const { data, isLoading, isFetching, isError } = useGetAthleteMarketplaceProfileQuery(
    { id, campaignId },
    { skip: !id },
  )
  const [setShortlisted, { isLoading: savingShortlist }] = useSetShortlistedMutation()
  const [createDirectMessage, { isLoading: isSendingDM }] = useCreateDirectMessageThreadMutation()
  const [createCollaboration, { isLoading: isCreating }] = useCreateCollaborationMutation()

  // ?offer=1 (Make offer from the Marketplace) opens the offer form straight away
  // "Book this" from a shared shop link comes back here as ?book=… once they've signed in
  const linkedPick = decodePick(searchParams.get('book'))
  const [offerOpen, setOfferOpen] = useState(
    () => searchParams.get('offer') === '1' || !!linkedPick,
  )
  // "Book this" in the shop: the series and format picked, filled into the booking
  const [pick, setPick] = useState<BookPick | null>(linkedPick)

  if (isLoading || (!data && isFetching)) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-[1280px] space-y-5 p-4 sm:p-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </MainLayout>
    )
  }
  if (isError || !data) {
    return (
      <MainLayout>
        <div className="mx-auto max-w-md p-6 text-center">
          <h2 className="mb-4 text-2xl font-bold text-foreground">Athlete not found</h2>
          <Button onClick={() => navigate('/marketplace')}>Back to the Marketplace</Button>
        </div>
      </MainLayout>
    )
  }

  const first = data.athlete.name.split(' ')[0]
  // The offer and message forms take the Marketplace card's shape
  const athlete = {
    id: data.athlete.id,
    name: data.athlete.name,
    avatar: data.athlete.avatar,
    school: data.athlete.school,
    sports: data.athlete.sports,
  } as unknown as Athlete

  const chooseCampaign = (value: string) =>
    setSearchParams(value === NO_CAMPAIGN ? {} : { campaignId: value }, { replace: true })

  const toggleShortlist = async () => {
    try {
      const res = await setShortlisted({ id, on: !data.shortlisted }).unwrap()
      toast({
        title: res.shortlisted
          ? `${first} saved to your shortlist`
          : 'Removed from your shortlist',
      })
    } catch {
      toast({ title: "Couldn't update your shortlist", variant: 'destructive' })
    }
  }

  const share = async () => {
    // The shop's public link when they're open to brands: it opens without an account
    const open = !!data?.shopUrl && data.availability.openToOffers
    const url = open ? data!.shopUrl! : window.location.href
    try {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: open
          ? 'Anyone can open the shop, even without an account.'
          : 'Anyone on your team can open it.',
      })
    } catch {
      toast({ title: 'Copy this link', description: url })
    }
  }

  const sendMessage = async (msg: Pick<DirectMessageData, 'message'>) => {
    try {
      await createDirectMessage({ athleteId: data.athlete.id, message: msg.message }).unwrap()
      toast({ title: 'Message sent', description: `${first} will see it in their messages.` })
      setOfferOpen(false)
      navigate('/messages')
    } catch (err: any) {
      toast({
        title: "Couldn't send",
        description: err?.data?.message || err?.data?.error || 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  const sendOffer = async (offerData: OfferData) => {
    if (!campaignId) {
      toast({
        title: 'Pick a campaign',
        description: 'Every booking belongs to a campaign. Choose one in the Message step.',
        variant: 'destructive',
      })
      return
    }
    try {
      const response = await createCollaboration({
        campaignId,
        athleteId: data.athlete.id,
        ...offerRequestBody(offerData),
      }).unwrap()
      toast({
        title: 'Booking request sent',
        description: `${first} can accept, counter or pass.`,
      })
      setOfferOpen(false)
      if (response?.id) navigate(`/campaign/${campaignId}/athlete/${response.id}`)
    } catch (err: any) {
      toast({
        title: "Couldn't send the booking request",
        description: err?.data?.message || err?.data?.error || err?.message || 'Try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1280px] space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />{' '}
            {application ? 'Back to Incoming' : 'Back to the Marketplace'}
          </button>
          <label className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <span className="hidden sm:inline">Viewing for campaign</span>
            <Select value={campaignId ?? NO_CAMPAIGN} onValueChange={chooseCampaign}>
              <SelectTrigger className="h-9 w-56 max-w-[70vw] bg-card" aria-label="Campaign">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CAMPAIGN}>No campaign</SelectItem>
                {openCampaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <MarketplaceProfile
          data={data}
          onBook={(p) => {
            setPick(p)
            setOfferOpen(true)
          }}
          actions={
            <>
              {application ? (
                <div className="space-y-3 rounded-xl border border-[#BFD7FB] bg-[#EFF6FF] p-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#1D4ED8]">
                      {application.source === 'pitch'
                        ? `${first} pitched you`
                        : application.source === 'counter'
                          ? `${first} countered your offer`
                          : application.source === 'quote'
                            ? `${first}'s rate`
                            : `${first} applied`}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-foreground">
                      {application.title}
                    </p>
                    <p className="text-sm text-foreground">
                      {application.dealTypeLabel} ·{' '}
                      <span className="font-semibold">
                        {application.ask != null ? money(application.ask) : 'Product only'}
                      </span>
                      {application.statedPay != null &&
                      application.ask != null &&
                      application.statedPay !== application.ask ? (
                        <span className="text-muted-foreground">
                          {' '}
                          (you {application.source === 'counter' ? 'offered' : 'posted'}{' '}
                          {money(application.statedPay)})
                        </span>
                      ) : null}
                    </p>
                    {application.message ? (
                      <p className="mt-1 line-clamp-4 text-sm text-muted-foreground">
                        “{application.message}”
                      </p>
                    ) : null}
                  </div>
                  <ApplicationActions
                    applicationId={application.id}
                    athleteName={application.athlete.name ?? first}
                    dealType={application.dealType}
                    ask={application.ask}
                    stacked
                    onDone={(status) =>
                      navigate(
                        status === 'accepted'
                          ? application.campaignId
                            ? `/campaign/${application.campaignId}/athlete/${application.id}`
                            : `/collaborations/${application.id}`
                          : '/campaigns?tab=incoming',
                      )
                    }
                  />
                </div>
              ) : (
                <Button
                  variant="lime"
                  className="h-11 w-full text-base"
                  onClick={() => setOfferOpen(true)}
                >
                  <Send className="h-4 w-4" /> Book {first} <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="outline"
                className={cn('h-11 w-full bg-card', data.shortlisted && 'border-[#9BD85A]')}
                onClick={toggleShortlist}
                disabled={savingShortlist}
                aria-pressed={data.shortlisted}
              >
                <Bookmark
                  className={cn('h-4 w-4', data.shortlisted && 'fill-[#3F7A0A] text-[#3F7A0A]')}
                />
                {data.shortlisted ? 'On your shortlist' : 'Save to shortlist'}
              </Button>
              <Button variant="outline" className="h-11 w-full bg-card" onClick={share}>
                <Share2 className="h-4 w-4" /> Share shop
              </Button>
            </>
          }
        />
      </div>

      <SendOfferModal
        open={offerOpen}
        onOpenChange={(o) => {
          setOfferOpen(o)
          if (!o) setPick(null)
        }}
        pick={pick}
        athlete={athlete}
        campaignName={campaign?.name}
        subtitle={
          campaign
            ? [campaign.name, campaign.brand?.name].filter(Boolean).join(' · ')
            : 'Pick the campaign this is for'
        }
        onSubmit={sendOffer}
        onSendMessage={(message) => sendMessage({ message })}
        isSending={isCreating || isSendingDM}
        beforeMessage={
          <div>
            <label className="mb-2 block font-semibold text-foreground">Campaign</label>
            <Select value={campaignId ?? ''} onValueChange={(v) => chooseCampaign(v)}>
              <SelectTrigger className="h-12 bg-card" aria-label="Campaign for this offer">
                <SelectValue placeholder="Pick a campaign" />
              </SelectTrigger>
              <SelectContent>
                {openCampaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!openCampaigns.length ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Offers belong to a campaign.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/campaign/create')}
                  className="font-semibold text-[#3F7A0A] underline"
                >
                  Create one
                </button>
              </p>
            ) : null}
          </div>
        }
      />
    </MainLayout>
  )
}

export default AthleteProfile
